import { Alert } from '@mui/material';
import { useNavigate } from 'react-router';

import { url } from '../../appRouter/url';
import { VeranstaltungRessourcenCalendar } from '../../components/general';
import {
  generateCreateVeranstaltungErrorSnackbar,
  generateCreateVeranstaltungErrorSnackbarWithFehlerId,
  generateCreateVeranstaltungSuccessSnackbar,
} from '../../components/snackbars';
import { generateKurzfristigerErsatzterminInfoSnackbar } from '../../components/snackbars/createVeranstaltungSnackbars.tsx';
import { useSnackbarFromGenerated } from '../../components/snackbars/useSnackbarFromGenerated.ts';
import {
  type CreateVeranstaltungInput,
  type CreateVeranstaltungOutput,
  cservOnlineTools,
  type RessourcenBlockungen,
  terminart,
  type Terminart,
  type ThemaFromThemaRouter,
  type Zeitraum,
} from '../../dtos';
import { trpc } from '../../trpc';
import { isOnlineThema, isTermingruppe, sessionStorageApi, sessionStorageKeys } from '../../utils';
import { ablaufStartsWithin11DaysAfterDate } from './CreateVeranstaltung.utils.ts';

type CreateVeranstaltungProps = {
  readonly thema: Pick<
    ThemaFromThemaRouter,
    | 'akaThemaId'
    | 'planungsinformation'
    | 'isOnline'
    | 'planungsformat'
    | 'module'
    | 'manuellerAusschreibungsprozess'
    | 'veranstaltungstypSapId'
  >;
};

export const CreateVeranstaltung = (props: CreateVeranstaltungProps) => {
  const { thema } = props;

  const { enqueueSnackbarFromGenerated } = useSnackbarFromGenerated();
  const navigate = useNavigate();

  if (isTermingruppe(thema.planungsformat)) {
    return (
      <Alert severity="warning">
        Das strukturelle Format dieses Themas sieht keine Anlage einer gewöhnlichen Veranstaltung vor!
      </Alert>
    );
  }

  const createVeranstaltungMutation = trpc.veranstaltung.createVeranstaltung.useMutation({
    onSuccess: (output: CreateVeranstaltungOutput) => {
      enqueueSnackbarFromGenerated(generateCreateVeranstaltungSuccessSnackbar(output.akaVeranstaltungId));

      if (output.terminart === terminart.ERSATZ && ablaufStartsWithin11DaysAfterDate(output.ablauf, new Date())) {
        enqueueSnackbarFromGenerated(generateKurzfristigerErsatzterminInfoSnackbar(output.akaVeranstaltungId));
      }
    },
    onError: (error) => {
      const fehlerId = sessionStorageApi.get(sessionStorageKeys.LATEST_X_VIVA_REQUEST_ID);

      enqueueSnackbarFromGenerated(
        fehlerId === null
          ? generateCreateVeranstaltungErrorSnackbar(error)
          : generateCreateVeranstaltungErrorSnackbarWithFehlerId({ error, fehlerId }),
      );
    },
  });

  const onSave = async (
    blockungen: RessourcenBlockungen,
    ablauf: Zeitraum[],
    terminart: NonNullable<Terminart>,
  ): Promise<void> => {
    const input: CreateVeranstaltungInput = {
      akaThemaId: thema.akaThemaId,
      blockungen,
      terminart,
      onlineTool: isOnlineThema(thema) ? cservOnlineTools.ZOOM : null,
      ablauf,
      isTerminreihe: Boolean(thema.planungsinformation?.istTerminreihe),
    };

    await createVeranstaltungMutation.mutateAsync(input);
  };

  const onCancel = async (): Promise<void> => {
    await navigate(url.thema.details(thema.akaThemaId));
  };

  return (
    <VeranstaltungRessourcenCalendar
      onSave={onSave}
      onCancel={onCancel}
      thema={thema}
      isFullscreen={false}
      // eslint-disable-next-line @eslint-react/purity -- added during migration to ban-pdk. please solve issue or explain disable-reason here
      initDate={thema.planungsinformation?.planungszeitraum?.start ?? new Date()}
      veranstaltung={null}
    />
  );
};
